import type { TimezoneEntry, TimezoneOption } from "./timezone.types"

const timezoneEntries: TimezoneEntry[] = [
  // UTC
  { value: "UTC", label: "UTC", offset: "UTC+0" },

  // North America
  { value: "Pacific/Honolulu", label: "Hawaii", offset: "UTC-10" },
  { value: "America/Anchorage", label: "Alaska", offset: "UTC-9" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)", offset: "UTC-8" },
  { value: "America/Denver", label: "Mountain Time (US)", offset: "UTC-7" },
  { value: "America/Phoenix", label: "Arizona", offset: "UTC-7" },
  { value: "America/Chicago", label: "Central Time (US)", offset: "UTC-6" },
  { value: "America/New_York", label: "Eastern Time (US)", offset: "UTC-5" },

  // Canada
  {
    value: "America/Halifax",
    label: "Atlantic Time (Canada)",
    offset: "UTC-4",
  },
  { value: "America/St_Johns", label: "Newfoundland", offset: "UTC-3:30" },

  // Central & South America
  { value: "America/Sao_Paulo", label: "São Paulo", offset: "UTC-3" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "Buenos Aires",
    offset: "UTC-3",
  },
  { value: "America/Santiago", label: "Santiago", offset: "UTC-3" },
  { value: "America/Bogota", label: "Bogotá", offset: "UTC-5" },
  { value: "America/Mexico_City", label: "Mexico City", offset: "UTC-6" },
  { value: "America/Lima", label: "Lima", offset: "UTC-5" },

  // Europe
  { value: "Atlantic/Reykjavik", label: "Reykjavík", offset: "UTC+0" },
  { value: "Europe/London", label: "London", offset: "UTC+0" },
  { value: "Europe/Dublin", label: "Dublin", offset: "UTC+0" },
  { value: "Europe/Lisbon", label: "Lisbon", offset: "UTC+0" },
  { value: "Europe/Paris", label: "Paris", offset: "UTC+1" },
  { value: "Europe/Berlin", label: "Berlin", offset: "UTC+1" },
  { value: "Europe/Madrid", label: "Madrid", offset: "UTC+1" },
  { value: "Europe/Rome", label: "Rome", offset: "UTC+1" },
  { value: "Europe/Amsterdam", label: "Amsterdam", offset: "UTC+1" },
  { value: "Europe/Brussels", label: "Brussels", offset: "UTC+1" },
  { value: "Europe/Zurich", label: "Zurich", offset: "UTC+1" },
  { value: "Europe/Vienna", label: "Vienna", offset: "UTC+1" },
  { value: "Europe/Warsaw", label: "Warsaw", offset: "UTC+1" },
  { value: "Europe/Stockholm", label: "Stockholm", offset: "UTC+1" },
  { value: "Europe/Oslo", label: "Oslo", offset: "UTC+1" },
  { value: "Europe/Copenhagen", label: "Copenhagen", offset: "UTC+1" },
  { value: "Europe/Athens", label: "Athens", offset: "UTC+2" },
  { value: "Europe/Bucharest", label: "Bucharest", offset: "UTC+2" },
  { value: "Europe/Helsinki", label: "Helsinki", offset: "UTC+2" },
  { value: "Europe/Kyiv", label: "Kyiv", offset: "UTC+2" },
  { value: "Europe/Istanbul", label: "Istanbul", offset: "UTC+3" },
  { value: "Europe/Moscow", label: "Moscow", offset: "UTC+3" },

  // Africa
  { value: "Africa/Cairo", label: "Cairo", offset: "UTC+2" },
  { value: "Africa/Johannesburg", label: "Johannesburg", offset: "UTC+2" },
  { value: "Africa/Lagos", label: "Lagos", offset: "UTC+1" },
  { value: "Africa/Nairobi", label: "Nairobi", offset: "UTC+3" },
  { value: "Africa/Casablanca", label: "Casablanca", offset: "UTC+1" },

  // Middle East
  { value: "Asia/Dubai", label: "Dubai", offset: "UTC+4" },
  { value: "Asia/Riyadh", label: "Riyadh", offset: "UTC+3" },
  { value: "Asia/Tehran", label: "Tehran", offset: "UTC+3:30" },
  { value: "Asia/Jerusalem", label: "Jerusalem", offset: "UTC+2" },

  // Asia
  { value: "Asia/Karachi", label: "Karachi", offset: "UTC+5" },
  { value: "Asia/Kolkata", label: "Kolkata", offset: "UTC+5:30" },
  { value: "Asia/Dhaka", label: "Dhaka", offset: "UTC+6" },
  { value: "Asia/Bangkok", label: "Bangkok", offset: "UTC+7" },
  { value: "Asia/Jakarta", label: "Jakarta", offset: "UTC+7" },
  { value: "Asia/Singapore", label: "Singapore", offset: "UTC+8" },
  { value: "Asia/Hong_Kong", label: "Hong Kong", offset: "UTC+8" },
  { value: "Asia/Shanghai", label: "Shanghai", offset: "UTC+8" },
  { value: "Asia/Taipei", label: "Taipei", offset: "UTC+8" },
  { value: "Asia/Seoul", label: "Seoul", offset: "UTC+9" },
  { value: "Asia/Tokyo", label: "Tokyo", offset: "UTC+9" },

  // Australia & Pacific
  { value: "Australia/Perth", label: "Perth", offset: "UTC+8" },
  { value: "Australia/Adelaide", label: "Adelaide", offset: "UTC+9:30" },
  { value: "Australia/Sydney", label: "Sydney", offset: "UTC+10" },
  { value: "Australia/Brisbane", label: "Brisbane", offset: "UTC+10" },
  { value: "Australia/Melbourne", label: "Melbourne", offset: "UTC+10" },
  { value: "Pacific/Auckland", label: "Auckland", offset: "UTC+12" },
  { value: "Pacific/Fiji", label: "Fiji", offset: "UTC+12" },
]

/**
 * Timezone options formatted for select dropdowns.
 * Each option has an IANA timezone identifier as the value
 * and a user-friendly label with UTC offset.
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = timezoneEntries.map(
  ({ value, label, offset }) => ({
    value,
    label: `(${offset}) ${label}`,
  })
)

/**
 * Get a user-friendly display label for an IANA timezone identifier.
 * Returns the formatted label (e.g. "(UTC-5) Eastern Time (US)") if found,
 * or the raw IANA identifier if not in the list.
 */
export const getTimezoneLabel = (ianaTimezone: string): string => {
  const option = TIMEZONE_OPTIONS.find((opt) => opt.value === ianaTimezone)
  return option?.label ?? ianaTimezone
}
